import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Show, UserButton, useAuth } from "@clerk/react";
import { Heart, ShoppingBag, Search, Menu, X, Phone, MapPin, Instagram, Youtube, Facebook, User, LayoutDashboard, Package } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "./Brand";
import { useShop } from "@/lib/store";
import { useAuthedGet, waLink } from "@/lib/api";

const NAV = [["/shop", "Shop"], ["/anime-after-dark", "Anime After Dark"], ["/custom-printing", "Custom Studio"], ["/upload-design", "Upload Design"], ["/wholesale", "Wholesale"], ["/reels", "Reels"]];

function Announcement() {
  const { settings } = useShop();
  const t = settings.announcement || "WEAR YOUR VIBE. PRINT YOUR IDENTITY.";
  return (
    <div className="bg-neon text-black overflow-hidden py-2 text-[11px] font-mono font-semibold tracking-[.2em]" data-testid="announcement-bar">
      <div className="marquee">{[0, 1, 2, 3].map((i) => <span key={i} className="px-10">{t}  ✦</span>)}</div>
    </div>
  );
}

function SearchBox({ onDone }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); nav(`/shop?q=${encodeURIComponent(q)}`); onDone?.(); }} className="flex items-center border border-[#333] focus-within:border-neon transition-colors">
      <Search className="w-4 h-4 ml-3 text-grey" />
      <input data-testid="nav-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search hoodies, anime, jeans…" className="bg-transparent px-3 py-2 text-sm w-full outline-none" />
    </form>
  );
}

function AdminLink() {
  const { data } = useAuthedGet("/me");
  if (data?.role !== "admin") return null;
  return <Link to="/admin" data-testid="nav-admin-link" className="hover:text-neon transition-colors" title="Admin"><LayoutDashboard className="w-5 h-5" /></Link>;
}

function Navbar() {
  const { count, wish } = useShop();
  const { isSignedIn } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(false);
  return (
    <header className="sticky top-0 z-50 bg-[#080808]/85 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-8 h-16 flex items-center gap-6">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild><button className="lg:hidden" data-testid="mobile-menu-button" aria-label="Menu"><Menu /></button></SheetTrigger>
          <SheetContent side="left" className="bg-[#080808] border-r border-white/10 w-[85vw] max-w-sm">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <div className="mt-6"><SearchBox onDone={() => setOpen(false)} /></div>
            <nav className="mt-8 flex flex-col">
              {[...NAV, ["/wishlist", "Wishlist"], ["/orders", "My Orders"], ["/contact", "Contact"]].map(([to, l]) => (
                <NavLink key={to} to={to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${to.slice(1)}`} className="font-display text-3xl py-3 border-b border-white/10 hover:text-neon">{l}</NavLink>
              ))}
            </nav>
          </SheetContent>
        </Sheet>
        <Logo />
        <nav className="hidden lg:flex items-center gap-7 ml-6 text-[12px] uppercase tracking-[.14em] font-semibold">
          {NAV.map(([to, l]) => (
            <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}`} className={({ isActive }) => `relative py-1 transition-colors hover:text-neon ${isActive ? "text-neon" : "text-white/80"}`}>{l}</NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-5">
          <button onClick={() => setSearch((s) => !s)} data-testid="nav-search-toggle" className="hidden sm:block hover:text-neon" aria-label="Search">{search ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}</button>
          <Link to="/wishlist" data-testid="nav-wishlist" className="relative hover:text-neon hidden sm:block"><Heart className="w-5 h-5" />{wish.length > 0 && <span className="absolute -top-2 -right-2 bg-white text-black text-[10px] w-4 h-4 grid place-items-center font-bold">{wish.length}</span>}</Link>
          <Link to="/cart" data-testid="nav-cart" className="relative hover:text-neon"><ShoppingBag className="w-5 h-5" /><span data-testid="cart-count" className="absolute -top-2 -right-2 bg-neon text-black text-[10px] min-w-4 h-4 px-1 grid place-items-center font-bold">{count}</span></Link>
          {isSignedIn && <AdminLink />}
          {isSignedIn && <Link to="/orders" data-testid="nav-orders" className="hover:text-neon hidden sm:block" title="My orders"><Package className="w-5 h-5" /></Link>}
          <Show when="signed-in"><UserButton /></Show>
          <Show when="signed-out"><Link to="/sign-in" data-testid="nav-sign-in" className="hover:text-neon flex items-center gap-1 text-xs uppercase tracking-widest font-semibold"><User className="w-5 h-5" /><span className="hidden md:inline">Sign in</span></Link></Show>
        </div>
      </div>
      {search && <div className="max-w-[1500px] mx-auto px-4 sm:px-8 pb-4 reveal"><SearchBox onDone={() => setSearch(false)} /></div>}
    </header>
  );
}

function Footer() {
  const { settings: s } = useShop();
  const col = (title, links) => (
    <div>
      <p className="eyebrow mb-5">{title}</p>
      <ul className="space-y-3 text-sm text-white/70">{links.map(([to, l]) => <li key={l}><Link to={to} className="hover:text-neon transition-colors">{l}</Link></li>)}</ul>
    </div>
  );
  return (
    <footer className="bg-[#050505] border-t border-white/10 mt-24" data-testid="site-footer">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-8 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <Logo size="text-5xl" />
          <p className="font-mono text-xs tracking-[.25em] text-grey mt-4">WEAR YOUR VIBE. PRINT YOUR IDENTITY.</p>
          <div className="mt-8 space-y-3 text-sm text-white/80">
            <p className="flex gap-3"><MapPin className="w-4 h-4 text-neon shrink-0 mt-0.5" />{s.address || "128/1/4/3, Yashoda Nagar, Kanpur, Uttar Pradesh, India"}</p>
            <a href={`tel:+91${s.phone || "9214356156"}`} data-testid="footer-call-link" className="flex gap-3 hover:text-neon"><Phone className="w-4 h-4 text-neon" />+91 {s.phone || "9214356156"}</a>
          </div>
          <div className="flex gap-4 mt-6">
            {[[s.instagram, Instagram, "instagram"], [s.facebook, Facebook, "facebook"], [s.youtube, Youtube, "youtube"]].map(([href, I, k]) => href && (
              <a key={k} href={href} target="_blank" rel="noreferrer" data-testid={`footer-social-${k}`} className="w-10 h-10 border border-white/20 grid place-items-center hover:bg-neon hover:text-black transition-colors"><I className="w-4 h-4" /></a>
            ))}
          </div>
        </div>
        {col("Shop", [["/shop/oversized-t-shirts", "Oversized Tees"], ["/shop/hoodies", "Hoodies"], ["/shop/baggy-fit-jeans", "Baggy Jeans"], ["/shop/cargo-pants", "Cargo Pants"], ["/anime-after-dark", "Anime After Dark"]])}
        {col("Create", [["/custom-printing", "Custom Printing"], ["/upload-design", "Upload Your Design"], ["/wholesale", "B2B Wholesale"], ["/reels", "The Den Experience"]])}
        {col("Help", [["/contact", "Contact Us"], ["/orders", "Order History"], ["/wishlist", "Wishlist"], ["/cart", "Cart"]])}
      </div>
      <div className="border-t border-white/10 py-6 text-center font-mono text-[11px] text-grey tracking-widest">© {new Date().getFullYear()} DEN & THREADS · KANPUR, INDIA · D2C · B2B · WHOLESALE · CUSTOM PRINTING</div>
    </footer>
  );
}

export default function Layout() {
  const { settings } = useShop();
  return (
    <div className="min-h-screen flex flex-col">
      <Announcement />
      <Navbar />
      <main className="flex-1"><Outlet /></main>
      <Footer />
      <a href={waLink(settings.whatsapp)} target="_blank" rel="noreferrer" data-testid="whatsapp-float-button" aria-label="WhatsApp"
        className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-[#25D366] grid place-items-center shadow-[0_10px_30px_rgba(37,211,102,.35)] hover:scale-110 transition-transform">
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white"><path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5 2.5 1 3 .8 3.6.7.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.6-.4zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2z" /></svg>
      </a>
    </div>
  );
}
